import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Visibility as VisibilityIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LinkedIn as LinkedInIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

const ViewApplicants = () => {
  const [applicants] = useState([
    {
      id: 1,
      name: 'John Doe',
      email: 'john.doe@example.com',
      phone: '+1 (555) 123-4567',
      experience: '5 years',
      status: 'pending',
      appliedDate: '2023-05-15',
      resumeUrl: '#',
      linkedIn: 'https://linkedin.com/in/johndoe',
    },
    {
      id: 2,
      name: 'Jane Smith',
      email: 'jane.smith@example.com',
      phone: '+1 (555) 987-6543',
      experience: '3 years',
      status: 'interviewed',
      appliedDate: '2023-05-14',
      resumeUrl: '#',
      linkedIn: 'https://linkedin.com/in/janesmith',
    },
  ]);

  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const handleViewDetails = (applicant) => {
    setSelectedApplicant(applicant);
    setDetailsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedApplicant(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'interviewed':
        return 'info';
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Applicant',
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ mr: 2 }}>{params.value.charAt(0)}</Avatar>
          {params.value}
        </Box>
      ),
    },
    { field: 'experience', headerName: 'Experience', width: 130 },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    { field: 'appliedDate', headerName: 'Applied Date', width: 130 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              onClick={() => handleViewDetails(params.row)}
              size="small"
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download Resume">
            <IconButton
              onClick={() => window.open(params.row.resumeUrl)}
              size="small"
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Send Email">
            <IconButton
              onClick={() => window.location.href = `mailto:${params.row.email}`}
              size="small"
            >
              <EmailIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Job Applicants
      </Typography>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" color="primary">
                Position
              </Typography>
              <Typography>Senior React Developer</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" color="primary">
                Total Applicants
              </Typography>
              <Typography>{applicants.length}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" color="primary">
                Posted Date
              </Typography>
              <Typography>May 10, 2023</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" color="primary">
                Status
              </Typography>
              <Chip label="Active" color="success" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <DataGrid
        rows={applicants}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[10]}
        autoHeight
        disableSelectionOnClick
        sx={{
          backgroundColor: 'background.paper',
          boxShadow: 1,
          borderRadius: 1,
          p: 1,
        }}
      />

      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedApplicant && (
          <>
            <DialogTitle>
              Applicant Details - {selectedApplicant.name}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EmailIcon sx={{ mr: 1 }} />
                    <Typography>{selectedApplicant.email}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PhoneIcon sx={{ mr: 1 }} />
                    <Typography>{selectedApplicant.phone}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LinkedInIcon sx={{ mr: 1 }} />
                    <Typography>
                      <a
                        href={selectedApplicant.linkedIn}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        LinkedIn Profile
                      </a>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Application Status
                  </Typography>
                  <Chip
                    label={selectedApplicant.status.charAt(0).toUpperCase() + 
                          selectedApplicant.status.slice(1)}
                    color={getStatusColor(selectedApplicant.status)}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      href={selectedApplicant.resumeUrl}
                      fullWidth
                    >
                      Download Resume
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default ViewApplicants;